import { useBooleanState } from '@cian/react-utils';
import { Image } from '@cian/ui-kit';

import { clsx } from 'clsx';
import { type FC, useEffect, useRef } from 'react';

import styles from './BackgroundVideo.css';

export interface IBackgroundVideoProps {
  url: string;
  poster: string;
  active: boolean;
}

export const BackgroundVideo: FC<IBackgroundVideoProps> = ({ url, poster, active }) => {
  const { state: isLoaded, setTrue: setLoadedState } = useBooleanState();
  const { state: isError, setTrue: setErrorState } = useBooleanState();

  const videoRef = useRef<HTMLVideoElement>(null);

  const handleLoaded = (): void => setLoadedState();
  const handleError = (): void => {
    setErrorState();
    setLoadedState();
  };

  useEffect(() => {
    if (!videoRef.current || !isLoaded) {
      return;
    }

    if (active) {
      videoRef.current.play();
    } else {
      videoRef.current.pause();
      videoRef.current.currentTime = 0;
    }
  }, [active, isLoaded]);

  return (
    <div className={styles['container']} data-testid="background-video-container">
      <video
        loop
        muted
        playsInline
        className={styles['video']}
        data-testid="background-video"
        ref={videoRef}
        onCanPlayThrough={handleLoaded}
        onError={handleError}
      >
        <source src={url} type="video/mp4" />
      </video>
      <div
        className={clsx(styles['poster'], isLoaded && !isError && styles['poster--hidden'])}
        data-testid="background-video-poster"
      >
        <Image objectFit="cover" src={poster} />
      </div>
    </div>
  );
};
